/**
 * AdminReauthModal — re-elevate the current session to admin scope.
 *
 * Admin mutations (member create/edit/archive, force-reload) require a session
 * with `role_scope='admin'` AND a fresh 10-minute window (requireAdminScope).
 * That scope is granted at onboarding-finish, then expires. This modal is the
 * ONLY way to re-acquire it from the UI: it POSTs the admin's OWN password
 * (#143 — there is no shared household password any more) to
 * /api/auth/admin-password and, on success, the server re-grants the scope.
 *
 * Callers open it when a mutation fails with a 403 admin-scope error
 * (see isAdminScopeError), then retry the original action from `onSuccess`.
 *
 * Errors:
 *   - 401 wrong_password / not_admin → inline "wrong password"
 *   - locked (Retry-After)           → inline "try again in N s"
 *   - 403 password_change_required   → the admin still has the inherited
 *     household password (#143); message + link to /velg-passord"
 */
import { createPortal } from 'react-dom';
import { useState } from 'react';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../../components/primitives';
import { CHOOSE_PASSWORD_PATH } from '../auth/ChooseOwnPasswordRoute';

export interface AdminReauthModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export function AdminReauthModal({ open, onClose, onSuccess }: AdminReauthModalProps): ReactElement | null {
  const { t } = useTranslation();
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [changeRequired, setChangeRequired] = useState(false);
  const [pending, setPending] = useState(false);

  if (!open) return null;

  async function submit(): Promise<void> {
    if (pending || password.length === 0) return;
    setPending(true);
    setError(null);
    setChangeRequired(false);
    try {
      const res = await fetch('/api/auth/admin-password', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password }),
      });
      if (res.ok) {
        setPassword('');
        onSuccess();
        return;
      }
      if (res.status === 403) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        if (body.error === 'password_change_required') {
          setChangeRequired(true);
          setError(t('admin.members.reauth.change_required'));
          return;
        }
      }
      const retryAfter = res.headers.get('Retry-After');
      if (retryAfter) {
        setError(t('admin.members.reauth.locked', { seconds: retryAfter }));
      } else {
        setError(t('admin.members.reauth.wrong'));
      }
    } catch {
      setError(t('error.offline'));
    } finally {
      setPending(false);
    }
  }

  function close(): void {
    setPassword('');
    setError(null);
    onClose();
  }

  // Portal: ligger modalen i et glasskort (backdrop-filter), blir kortet
  // ramma for `fixed`, og dimmingen dekker bare kortet (#159).
  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t('admin.members.reauth.title')}
      className="fixed inset-0 z-50 bg-dim flex items-center justify-center p-4"
      data-testid="admin-reauth-modal"
    >
      <div className="bg-sheet p-6 rounded-lg shadow-lg max-w-sm w-full space-y-4">
        <h2 className="font-display text-display-s text-ink">
          {t('admin.members.reauth.title')}
        </h2>
        <p className="text-sm text-ink-2">{t('admin.members.reauth.body')}</p>
        <form
          className="space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            void submit();
          }}
        >
          <label className="flex flex-col gap-1">
            <span className="sr-only">{t('admin.members.reauth.label')}</span>
            <input
              data-testid="admin-reauth-password"
              type="password"
              autoFocus
              autoComplete="current-password"
              className="border-card-line rounded-md border p-3"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={t('admin.members.reauth.label')}
            />
          </label>
          {error !== null && (
            <p className="text-sm text-bad" data-testid="admin-reauth-error">
              {error}
            </p>
          )}
          {changeRequired ? (
            // Full sidelasting med vilje: modalen brukes også uten router.
            <a
              href={CHOOSE_PASSWORD_PATH}
              data-testid="admin-reauth-change-password"
              className="text-primary text-sm underline"
            >
              {t('admin.members.reauth.change_link')}
            </a>
          ) : null}
          <div className="flex justify-end gap-3">
            <Button variant="ghost" onClick={close} data-testid="admin-reauth-cancel">
              {t('common.cancel')}
            </Button>
            <Button
              type="submit"
              disabled={pending || password.length === 0}
              data-testid="admin-reauth-submit"
            >
              {t('admin.members.reauth.submit')}
            </Button>
          </div>
        </form>
      </div>
    </div>,
    document.body,
  );
}
